import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, NavigationExtras, Router, RouterStateSnapshot } from '@angular/router';
import { catchError, from, map, Observable, of, switchMap, take, tap } from 'rxjs';
import { ActivityMonitorService, ApiGatewayService, AppContextService, WindowService } from '@drc-eca/eca-components-lib';
import { OktaService } from '../../core/services/okta.service';
import { environment } from '../../../environments/environment';
import { OktaTenantService } from './okta-tenant.service';

const OKTA_TOKEN_STORAGE = 'okta-token-storage';
// Written to sessionStorage immediately before we hand off to an external
// Okta/tenant signout redirect. Its presence on re-entry proves we already
// completed the external signout leg, even when the tenant drops our `state`
// query param on the way back. This is what actually breaks the logout loop.
const LOGOUT_SIGNOUT_PENDING = 'eca-logout-signout-pending';
// Only honor the pending marker briefly (the redirect round-trip takes seconds).
// A stale marker must never short-circuit a fresh, intentional logout.
const LOGOUT_SIGNOUT_PENDING_TTL_MS = 2 * 60 * 1000;

@Injectable({
  providedIn: 'root'
})
export class OktaReturnStateGuard  {
  private activityMonitorService = inject(ActivityMonitorService);
  private apiGatewayService = inject(ApiGatewayService);
  private appContextService = inject(AppContextService);
  private oktaService = inject(OktaService);
  private oktaTenantService = inject(OktaTenantService);
  private router = inject(Router);
  private windowService = inject(WindowService);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    this.activityMonitorService.stopMonitoring();
    this.appContextService.setToken(null as any);
    const url = this.windowService['location'].toString();
    return this.checkUrl(url, route);
  }

  checkUrl(url: string, route: ActivatedRouteSnapshot): Observable<boolean> {
    const uri = new URL(url);
    // Okta will return with state
    const state = uri.searchParams.get('state') || route.queryParams['state'];
    let returnedFromSignout = !!state;
    let client = '';
    let timeout = false;
    let returnUrl = '';
    if (route.queryParams['returnUrl']) {
      returnUrl = decodeURIComponent(decodeURIComponent(route.queryParams['returnUrl']));
    }
    if (state) {
      // if state is returned from Okta state should equal something like "client=DRCPORTAL;timeout=true"
      const segments = state.split(',');
      for (const segment of segments) {
        if (segment.includes('client')) {
          client = segment.split('=')[1];
        }
        if (segment.includes('timeout')) {
          timeout = segment.split('=')[1].toString() === 'true';
        }
        if (segment.includes('returnUrl')) {
          returnUrl = decodeURIComponent(decodeURIComponent(segment.split('=')[1]));
        }
      }
    }

    // Fall back to the durable marker when the signout redirect dropped `state`.
    // This restores the logout context and, crucially, flags that the external
    // signout already happened so we complete logout instead of looping.
    const pendingSignout = this.consumePendingSignout();
    if (pendingSignout) {
      returnedFromSignout = true;
      client = client || pendingSignout.client;
      timeout = timeout || pendingSignout.timeout;
      returnUrl = returnUrl || pendingSignout.returnUrl;
    }

    if (!client) {
      client = route.params['productCode'];
    }
    if (!timeout) {
      timeout = route.queryParams['timeout'] === 'true';
    }

    return this.resolveClientCode(client).pipe(
      switchMap((resolvedClient) => {
        // Returning from Okta/tenant signout is the final logout leg. Completing
        // local logout here prevents the sign-out redirect from looping.
        if (returnedFromSignout) {
          return this.completeLogout(resolvedClient, timeout, returnUrl);
        }

        const oktaStorage = sessionStorage.getItem(OKTA_TOKEN_STORAGE);
        const currentLocation = this.windowService['location'] as Location | string;
        const pathFromLocation = typeof currentLocation === 'string' ? new URL(currentLocation).pathname : currentLocation.pathname;
        const oktaRedirectUrl = this.buildOktaRedirectUrl(pathFromLocation);
        // TODO: When all environments use the new login flow and setup-config supplies
        // okta-saml-redirect-url for every relevant client, replace this env-based
        // default-tenant comparison with config-driven tenant resolution.
        const defaultTenantOrigin = new URL(environment.okta.samlRedirectUrl).origin;
        return from(this.oktaTenantService.getTenantOrigin(resolvedClient ? resolvedClient : undefined)).pipe(
          switchMap((tenantOrigin) => {
            if (tenantOrigin !== defaultTenantOrigin) {
              const signOutState = [];
              if (resolvedClient) {
                signOutState.push(`client=${resolvedClient}`);
              }
              if (timeout) {
                signOutState.push(`timeout=${timeout}`);
              }
              if (returnUrl) {
                signOutState.push(`returnUrl=${encodeURIComponent(returnUrl)}`);
              }
              this.markPendingSignout(resolvedClient, timeout, returnUrl);
              return from(this.oktaService.signOutOfTenant(tenantOrigin, oktaRedirectUrl, signOutState)).pipe(map(() => false));
            }

            if (oktaStorage) {
              const state = [];
              if (resolvedClient) {
                state.push(`client=${resolvedClient}`);
              }
              if (timeout) {
                state.push(`timeout=${timeout}`);
              }
              if (returnUrl) {
                state.push(`returnUrl=${encodeURIComponent(returnUrl)}`);
              }
              this.markPendingSignout(resolvedClient, timeout, returnUrl);
              // Okta will return here with it's session removed so no need to call ECA now.
              return from(this.oktaService.logout(oktaRedirectUrl, state)).pipe(map(() => false));
            }

            return this.logoutEca().pipe(
              map(() => {
                const navigate = ['login'];
                if (resolvedClient) {
                  navigate.push(resolvedClient);
                }
                this.appContextService.setSelectedProductCode(resolvedClient);
                if (!oktaStorage) {
                  sessionStorage.clear();
                }
                this.navigate(navigate, timeout, returnUrl);
                // Don't actually allow the navigation to logout
                return false;
              })
            );
          }),
          catchError(() => {
            if (oktaStorage) {
              const state = [];
              if (resolvedClient) {
                state.push(`client=${resolvedClient}`);
              }
              if (timeout) {
                state.push(`timeout=${timeout}`);
              }
              if (returnUrl) {
                state.push(`returnUrl=${encodeURIComponent(returnUrl)}`);
              }
              this.markPendingSignout(resolvedClient, timeout, returnUrl);
              return from(this.oktaService.logout(oktaRedirectUrl, state)).pipe(map(() => false));
            }
            return this.logoutEca().pipe(
              map(() => {
                const navigate = ['login'];
                if (resolvedClient) {
                  navigate.push(resolvedClient);
                }
                this.appContextService.setSelectedProductCode(resolvedClient);
                sessionStorage.clear();
                this.navigate(navigate, timeout, returnUrl);
                return false;
              })
            );
          })
        );
      })
    );
  }

  private resolveClientCode(client: string): Observable<string> {
    if (client) {
      return of(client);
    }

    return this.appContextService.getSelectedProductCode().pipe(
      take(1),
      map((selectedProductCode) => selectedProductCode || ''),
      catchError(() => of(''))
    );
  }

  private markPendingSignout(client: string, timeout: boolean, returnUrl: string): void {
    try {
      sessionStorage.setItem(
        LOGOUT_SIGNOUT_PENDING,
        JSON.stringify({ client, timeout, returnUrl, ts: Date.now() })
      );
    } catch {
      // Storage may be unavailable; the round-tripped `state` param remains the fallback.
    }
  }

  private consumePendingSignout(): { client: string; timeout: boolean; returnUrl: string } | null {
    let raw: string | null = null;
    try {
      raw = sessionStorage.getItem(LOGOUT_SIGNOUT_PENDING);
    } catch {
      return null;
    }
    if (!raw) {
      return null;
    }
    try {
      sessionStorage.removeItem(LOGOUT_SIGNOUT_PENDING);
    } catch {
      // ignore
    }
    try {
      const parsed = JSON.parse(raw) as { client?: string; timeout?: boolean; returnUrl?: string; ts?: number };
      if (typeof parsed.ts === 'number' && Date.now() - parsed.ts > LOGOUT_SIGNOUT_PENDING_TTL_MS) {
        return null;
      }
      return {
        client: parsed.client || '',
        timeout: !!parsed.timeout,
        returnUrl: parsed.returnUrl || ''
      };
    } catch {
      return null;
    }
  }

  private buildOktaRedirectUrl(pathname: string): string {
    const baseRedirectUri = `${environment.okta.clientConfig.redirectUri.replace(/\/$/, '')}/`;
    const normalizedPath = pathname.replace(/^\//, '');
    const callbackUrl = new URL(normalizedPath, baseRedirectUri).toString().replace(/\/$/, '');
    return `${callbackUrl}#/logout`;
  }

  private completeLogout(client: string, timeout: boolean, returnUrl: string): Observable<boolean> {
    return this.logoutEca().pipe(
      map(() => {
        const navigate = ['login'];
        if (client) {
          navigate.push(client);
        }
        this.appContextService.setSelectedProductCode(client);
        sessionStorage.clear();
        this.navigate(navigate, timeout, returnUrl);
        return false;
      })
    );
  }

  private logoutEca() {
    const apiConfig = this.apiGatewayService.makeApiConfigString();
    const url = `eca-security-service/${apiConfig}/v0/logout`;

    return this.appContextService.getToken().pipe(
      switchMap(token => {
        if (token) {
          return this.apiGatewayService.post<any>(url, { token: token })
        }
        return of(token);
      }),
      catchError(() => of(undefined)),
      tap(() => this.appContextService.setToken(null as any))
    );
  }

  private navigate(url: string[], timeout: boolean, returnUrl: string) {
    const params = {
      queryParams: {}
    } as NavigationExtras;
    if (timeout) {
      params.queryParams!.timeout = true;
    }
    if (returnUrl) {
      params.queryParams!.returnUrl = returnUrl;
    }
    if (Object.keys(params.queryParams!).length) {
      this.router.navigate(url, params);
    } else {
      this.router.navigate(url);
    }
  }
}
